import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateCorrectionDialog } from '@/features/memorization/create-correction-dialog';
import { EvaluationHistory } from '@/features/memorization/evaluation-history';
import { useCancelTarget, useTarget, useTargetCoverage } from '@/features/memorization/hooks';
import { TARGET_STATUS_LABELS } from '@/features/memorization/labels';
import { useSurahLookup } from '@/features/quran/hooks';
import { useHasCapability } from '@/lib/capabilities';

/**
 * Target detail (Phase G-1, Job 15) - shows only what the target contract
 * actually exposes: the target's own fields plus its live coverage read
 * (`GET .../coverage`). Setoran and Evaluation history are explicitly a
 * later phase; this page ends at the coverage percentage, not a fabricated
 * "view submissions" button.
 */
export function TargetDetailPage(): JSX.Element {
  const { studentId, targetId } = useParams<{ studentId: string; targetId: string }>();
  const navigate = useNavigate();
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const { data: target, isLoading, isError } = useTarget(targetId ?? '');
  const { data: coverage } = useTargetCoverage(targetId ?? '');
  const cancel = useCancelTarget(studentId ?? '');
  const canManage = useHasCapability('memorization_targets:manage');
  const canEvaluate = useHasCapability('evaluations:manage');
  const surahs = useSurahLookup();
  const surah = target !== undefined ? surahs.get(target.surah) : undefined;

  if (studentId === undefined || targetId === undefined) {
    return <p className="p-4 text-sm text-destructive">Target tidak ditemukan.</p>;
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      {isLoading && <Skeleton className="h-32 w-full" />}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat target hafalan.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && target !== undefined && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="flex flex-wrap items-baseline gap-x-2">
                <span>
                  {surah !== undefined
                    ? `${String(target.surah)}. ${surah.nameLatin}`
                    : `Surah ${String(target.surah)}`}
                  : Ayat {target.startAyah}
                  {target.endAyah !== target.startAyah ? `-${target.endAyah}` : ''}
                </span>
                {surah !== undefined && (
                  <span lang="ar" dir="rtl" className="text-base font-normal text-muted-foreground">
                    {surah.nameArabic}
                  </span>
                )}
              </CardTitle>
              <Badge>{TARGET_STATUS_LABELS[coverage?.status ?? target.status]}</Badge>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {target.targetDate !== null && (
                <p className="text-sm text-muted-foreground">
                  Tanggal target: {new Date(target.targetDate).toLocaleDateString('id-ID')}
                </p>
              )}
              {coverage !== undefined && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm text-muted-foreground">
                    Cakupan: {coverage.acceptedAyahs} dari {coverage.totalAyahs} ayat (
                    {coverage.coveragePercentage}%)
                  </p>
                  <Progress value={coverage.coveragePercentage} />
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Dibuat {new Date(target.createdAt).toLocaleDateString('id-ID')}
              </p>

              {canManage && target.status !== 'CANCELLED' && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" size="sm" className="w-fit">
                      Batalkan Target
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Batalkan target hafalan ini?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Target akan ditandai dibatalkan dan tidak lagi dihitung dalam progres santri.
                        Tindakan ini tidak dapat dibatalkan dari dalam aplikasi.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Batal</AlertDialogCancel>
                      <AlertDialogAction
                        disabled={cancel.isPending}
                        onClick={() => {
                          cancel.mutate(targetId, {
                            onSuccess: () => {
                              void navigate(`/students/${studentId}`);
                            },
                          });
                        }}
                      >
                        Batalkan Target
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle>Evaluasi</CardTitle>
              {canEvaluate && target.status !== 'CANCELLED' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCorrectionOpen(true);
                  }}
                >
                  Catat Koreksi
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <EvaluationHistory targetId={targetId} />
            </CardContent>
          </Card>

          <CreateCorrectionDialog
            studentId={target.studentId}
            targetId={targetId}
            open={correctionOpen}
            onOpenChange={setCorrectionOpen}
          />
        </>
      )}
    </div>
  );
}
